'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Lock, ChevronRight, Navigation } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@kit/ui/card';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { cn } from '@kit/ui/utils';

import type { LocationInfo } from '@kit/bookings';

import { ImageCarousel } from '../../_components/image-carousel';

interface LocationSelectorProps {
  locations: LocationInfo[];
  onLocationSelect: (locationId: string) => void;
  selectedLocationId?: string | null;
  onValidateCode: (locationId: string, code: string) => Promise<boolean>;
  /**
   * When true, collapse non-selected locations behind a "Change location"
   * button once one is picked. Used on native to keep the booking flow
   * above the fold. Defaults to false (always show all cards).
   */
  collapseOnSelect?: boolean;
  /** Gallery images per location id (court_locations.image_urls). */
  imagesByLocation?: Record<string, string[]>;
  /**
   * Locale-aware name/city overrides per location id. The shared LocationInfo
   * type (from the booking-slots service) carries no Arabic (_alt) fields, so
   * the booking form resolves them from the richer get_public_locations rows
   * and passes the active-locale label here. Absent entries fall back to the
   * LocationInfo primary (English) name/city.
   */
  localizedLabels?: Record<string, { name?: string; city?: string | null }>;
}

export function LocationSelector({
  locations,
  onLocationSelect,
  selectedLocationId,
  onValidateCode,
  collapseOnSelect = false,
  imagesByLocation,
  localizedLabels,
}: LocationSelectorProps) {
  const { t } = useTranslation('bookings');

  // Resolve the display name/city for a location, preferring the locale-aware
  // override (Arabic) when present, else the LocationInfo primary.
  const labelFor = (location: LocationInfo) => ({
    name: localizedLabels?.[location.id]?.name?.trim() || location.name,
    city:
      localizedLabels?.[location.id]?.city?.toString().trim() || location.city,
  });

  const [expandedCodeLocationId, setExpandedCodeLocationId] = useState<
    string | null
  >(null);
  const [codeInputs, setCodeInputs] = useState<Record<string, string>>({});
  const [validatingId, setValidatingId] = useState<string | null>(null);
  // When collapseOnSelect is on, the picker shows only the active location
  // until the user explicitly taps "Change location". `isManuallyExpanded`
  // tracks that user-initiated reveal and is cleared again the moment a card
  // is picked, so the list folds back into the pill instead of staying open
  // (mirrors LocationFunnelPicker.handleClubSelect).
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);

  const selectLocation = (locationId: string) => {
    setIsManuallyExpanded(false);
    onLocationSelect(locationId);
  };

  const handleCardClick = (location: LocationInfo) => {
    if (location.accessMode === 'code_only') {
      setExpandedCodeLocationId((prev) =>
        prev === location.id ? null : location.id,
      );
      return;
    }

    selectLocation(location.id);
  };

  const handleCodeSubmit = async (locationId: string) => {
    const code = codeInputs[locationId]?.trim();

    if (!code) {
      toast.error(t('locationCodeRequired', 'Please enter an access code'));
      return;
    }

    setValidatingId(locationId);

    try {
      const isValid = await onValidateCode(locationId, code);

      if (isValid) {
        setExpandedCodeLocationId(null);
        selectLocation(locationId);
      } else {
        toast.error(t('locationCodeInvalid', 'Invalid access code'));
      }
    } catch {
      toast.error(t('locationCodeError', 'Failed to validate code'));
    } finally {
      setValidatingId(null);
    }
  };

  const gridCols =
    locations.length === 2
      ? 'grid-cols-1 md:grid-cols-2'
      : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  const sortedLocations = [...locations].sort((a, b) =>
    labelFor(a).name.localeCompare(labelFor(b).name),
  );
  const isCollapsed =
    collapseOnSelect && !!selectedLocationId && !isManuallyExpanded;
  const visibleLocations = isCollapsed
    ? sortedLocations.filter((l) => l.id === selectedLocationId)
    : sortedLocations;

  // Collapsed pill: single compact row with name, optional Maps link, and
  // a "Change" affordance. Tapping anywhere on the pill expands the picker.
  if (isCollapsed) {
    const selected = visibleLocations[0];
    if (!selected) return null;
    const selectedLabel = labelFor(selected);
    return (
      <button
        type="button"
        onClick={() => setIsManuallyExpanded(true)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-primary/40 bg-card px-3 py-2.5 text-start shadow-sm transition-colors hover:border-primary hover:bg-primary/5"
      >
        <div className="flex min-w-0 items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate text-sm font-medium">
            {selectedLabel.name}
          </span>
          {selectedLabel.city && (
            <span className="truncate text-xs text-muted-foreground">
              · {selectedLabel.city}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {selected.googleMapsUrl && (
            <a
              href={selected.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={(e) => e.stopPropagation()}
              aria-label={t('viewLocation', 'Location')}
              title={t('viewLocation', 'Location')}
            >
              <Navigation className="h-3.5 w-3.5" />
            </a>
          )}
          <span className="text-xs font-medium text-primary">
            {t('change', { defaultValue: 'Change' })}
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-primary rtl:rotate-180" />
        </div>
      </button>
    );
  }

  return (
    <div className={cn('grid gap-4', gridCols)}>
      {visibleLocations.map((location) => {
        const isSelected = selectedLocationId === location.id;
        const isCodeExpanded = expandedCodeLocationId === location.id;
        const isCodeOnly = location.accessMode === 'code_only';
        const label = labelFor(location);

        return (
          <Card
            key={location.id}
            data-test="location-card"
            data-test-location-slug={location.slug}
            className={cn(
              'cursor-pointer transition-all hover:shadow-md',
              isSelected && 'ring-2 ring-primary',
            )}
            onClick={() => handleCardClick(location)}
          >
            <CardContent className="p-4">
              {/* Same gallery as the schools funnel and the court cards: arrows,
                  dots and thumb swipe, rather than a bare scroll strip. The
                  card is a <Card onClick>, so a tap on the photo still selects
                  the location while a swipe is swallowed. */}
              {(imagesByLocation?.[location.id]?.length ?? 0) > 0 && (
                <ImageCarousel
                  images={imagesByLocation![location.id]!}
                  alt={label.name}
                  className="mb-3 rounded-md"
                  priority={false}
                />
              )}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {isCodeOnly ? (
                      <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <h3 className="font-semibold leading-snug">{label.name}</h3>
                  </div>

                  {label.city && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {label.city}
                    </p>
                  )}

                  {location.address && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {location.address}
                    </p>
                  )}

                  {location.googleMapsUrl && (
                    <a
                      href={location.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Navigation className="h-3 w-3" />
                      <span>{t('viewLocation', 'Location')}</span>
                    </a>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  {!isCodeOnly && (
                    <ChevronRight className="h-4 w-4 text-muted-foreground rtl:rotate-180" />
                  )}
                </div>
              </div>

              {isCodeOnly && isCodeExpanded && (
                <div
                  className="mt-3 flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Input
                    type="text"
                    placeholder={t('enterAccessCode', 'Enter access code')}
                    value={codeInputs[location.id] ?? ''}
                    onChange={(e) =>
                      setCodeInputs((prev) => ({
                        ...prev,
                        [location.id]: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCodeSubmit(location.id);
                      }
                    }}
                    className="flex-1"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    disabled={validatingId === location.id}
                    onClick={() => handleCodeSubmit(location.id)}
                  >
                    {validatingId === location.id
                      ? t('validating', 'Validating...')
                      : t('submit', 'Submit')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
